/**
 * G11 / V31 routing intelligence: classify a task, then combine stated rules > learned evidence >
 * defaults. Pure and deterministic; the router applies headroom, reset-aware account choice and
 * machine load on top, and every decision carries an explanation.
 */
import { classifyTaskType, ROUTING_TASK_TYPE_LABEL, } from '@viko/protocol';
import { DAY } from './types.js';
// ---- classifier --------------------------------------------------------------------------------
/** Deterministic heuristics live in the protocol so daemon and browser classify identically. */
export function classifyTask(task) {
    return classifyTaskType(task.title, task.labels);
}
const HALF_LIFE = 30 * DAY;
function median(xs) {
    if (!xs.length)
        return undefined;
    const s = [...xs].sort((a, b) => a - b);
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}
export function statsFor(outcomes, now) {
    let w = 0;
    let wOk = 0;
    let corr = 0;
    let verified = 0;
    for (const o of outcomes) {
        const wt = 0.5 ** (Math.max(0, now - o.at) / HALF_LIFE);
        w += wt;
        if (o.verified) {
            wOk += wt;
            verified++;
        }
        corr += wt * Math.min(o.corrections ?? 0, 3) * 0.1;
    }
    // Laplace smoothing: two pseudo-outcomes keep tiny samples from looking certain.
    const score = Math.max(0, (wOk + 1) / (w + 2) - corr / (w + 2));
    const cost = outcomes.map((o) => o.costUsd).filter((x) => x !== undefined);
    const dur = outcomes.map((o) => o.durationMs).filter((x) => x !== undefined);
    const s = {
        n: outcomes.length,
        verified,
        score,
        successPct: outcomes.length ? Math.round((verified / outcomes.length) * 100) : 0,
    };
    const mc = median(cost);
    const md = median(dur);
    if (mc !== undefined)
        s.medianCostUsd = mc;
    if (md !== undefined)
        s.medianDurationMs = md;
    return s;
}
const other = (a) => (a === 'claude' ? 'codex' : 'claude');
const cap = (a) => (a === 'claude' ? 'Claude' : 'Codex');
const MARGIN = 0.05;
function ruleFor(task, type, rules) {
    const mine = rules.filter((r) => r.taskType === type);
    // A project-scoped rule beats a global one.
    return mine.find((r) => r.projectId === task.projectId) ?? mine.find((r) => !r.projectId);
}
export function machineMatches(m, ref) {
    const r = ref.toLowerCase();
    return m.id.toLowerCase() === r || m.name?.toLowerCase() === r;
}
export function decideRouting(task, cfg, now) {
    const taskType = classifyTask(task);
    const minSamples = cfg.prefs?.minSamples ?? 5;
    const learn = cfg.prefs?.learn ?? true;
    const similar = (cfg.outcomes ?? []).filter((o) => o.taskType === taskType);
    const evidence = {};
    for (const a of ['claude', 'codex']) {
        const xs = similar.filter((o) => o.agent === a);
        if (xs.length)
            evidence[a] = statsFor(xs, now);
    }
    const d = { taskType, evidence };
    const label = ROUTING_TASK_TYPE_LABEL[taskType];
    const rule = ruleFor(task, taskType, cfg.prefs?.rules ?? []);
    if (rule) {
        d.rule = rule;
        if (rule.agent) {
            d.agent = { order: [rule.agent, other(rule.agent)], source: 'rule', note: `rule '${label}'` };
        }
        if (rule.machine) {
            d.machine = { id: rule.machine, source: 'rule', note: `rule '${label}'` };
        }
    }
    if (learn && !d.agent) {
        const c = evidence.claude;
        const x = evidence.codex;
        if (c && x && c.n >= minSamples && x.n >= minSamples && Math.abs(c.score - x.score) >= MARGIN) {
            const win = c.score > x.score ? 'claude' : 'codex';
            d.agent = { order: [win, other(win)], source: 'learned', note: 'learned' };
        }
        else if (!c || !x || c.n < minSamples || x.n < minSamples) {
            const rec = (cfg.recommendations ?? []).find((r) => r.taskType === taskType && r.confidence !== 'low');
            if (rec) {
                d.agent = {
                    order: [rec.preferred.provider, other(rec.preferred.provider)],
                    source: 'learned',
                    note: `cost report (${rec.confidence} confidence)`,
                };
            }
        }
    }
    if (learn && !d.machine) {
        const byMachine = new Map();
        for (const o of similar)
            if (o.machineId)
                byMachine.set(o.machineId, [...(byMachine.get(o.machineId) ?? []), o]);
        const ranked = [...byMachine]
            .filter(([, xs]) => xs.length >= minSamples)
            .map(([id, xs]) => ({ id, s: statsFor(xs, now) }))
            .sort((a, b) => b.s.score - a.s.score || (a.id < b.id ? -1 : 1));
        const [best, second] = ranked;
        if (best && second && best.s.score - second.s.score >= MARGIN) {
            d.machine = { id: best.id, source: 'learned', note: 'learned' };
        }
    }
    // An endpoint-supplied hint stands in for prefs the hub cannot read (rules > learned > default).
    const hint = task.routingHint;
    if (hint) {
        const label = ROUTING_TASK_TYPE_LABEL[taskType];
        const note = hint.source === 'rule' ? `rule '${label}'` : 'learned';
        if (!d.agent && hint.agent)
            d.agent = { order: [hint.agent, other(hint.agent)], source: hint.source, note };
        if (!d.machine && hint.machine)
            d.machine = { id: hint.machine, source: hint.source, note };
    }
    return d;
}
/** The content-free hint an endpoint attaches to a task (absent when nothing decided). */
export function routingHintFor(task, cfg, now) {
    const { routingHint: _ignored, ...bare } = task;
    const d = decideRouting(bare, cfg, now);
    if (!d.agent && !d.machine)
        return undefined;
    const source = d.rule ? 'rule' : 'learned';
    return {
        ...(d.agent ? { agent: d.agent.order[0] } : {}),
        ...(d.machine?.id ? { machine: d.machine.id } : {}),
        source,
        at: now,
    };
}
/** "Chose Codex on Photon: rule 'image generation'; 82% success on similar tasks (14 of 17)". */
export function explainRouting(d, chosen, extra = {}) {
    const parts = [];
    const label = ROUTING_TASK_TYPE_LABEL[d.taskType];
    const ev = d.evidence[chosen.agent];
    if (d.agent && !extra.fellBackFrom && d.agent.order[0] === chosen.agent) {
        parts.push(d.agent.source === 'rule' ? d.agent.note : `learned preference for ${label}`);
    }
    else if (extra.fellBackFrom) {
        parts.push(`${cap(extra.fellBackFrom)} unavailable or low on headroom`);
    }
    if (d.machine &&
        !extra.machinePrefUnmet &&
        (chosen.machineId === d.machine.id ||
            chosen.machine.toLowerCase() === d.machine.id?.toLowerCase())) {
        if (!parts.length || d.machine.source !== d.agent?.source) {
            parts.push(d.machine.source === 'rule' ? d.machine.note : `learned machine for ${label}`);
        }
    }
    else if (extra.machinePrefUnmet) {
        parts.push(extra.machinePrefUnmet);
    }
    if (ev && ev.n > 0) {
        parts.push(`${ev.successPct}% success on similar tasks (${ev.verified} of ${ev.n})`);
    }
    if (!parts.length)
        parts.push(`no rule or history for ${label}; default order`);
    return `Chose ${cap(chosen.agent)} on ${chosen.machine}: ${parts.join('; ')}`;
}
//# sourceMappingURL=routing.js.map